import ReceivedReferenceUpload from "./ReceivedReferenceUpload";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as HistoryIcon } from "../../../assets/icons/HistoryIcon.svg";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import NewDateField from "../../DesignSystem/Inputs/NewDateField";
import NewMobileNumberInput from "../../DesignSystem/Inputs/NewMobileNumberInput";
import NewInputField from "../../DesignSystem/Inputs/NewInputField";
import NewInputTextArea from "../../DesignSystem/Inputs/NewTextArea";
import RadioGroup from "../../DesignSystem/Inputs/RadioGroup";
import ToggleButton from "../../DesignSystem/Inputs/ToggleButton";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import FileUpload from "../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import ReferenceEmailPreviewModal from "./ReferenceEmailPreviewModal";
import Divider from "../../DesignSystem/Layout/Divider";
import {
  isValidEmailList,
  isValidReferencePhone,
} from "./utils/referenceValidation";
import {
  buildDefaultReferenceMessageBody,
  removeReferenceMessageBodySignature,
} from "./utils/referenceMessageBody";
import ReferenceEditLogs from "./ReferenceEditLogs";
import {
  RECRUITMENT_REFERENCE_STATUSES,
  RECRUITMENT_SELECTABLE_REFERENCE_STATUSES,
  EDUCATIONAL_REFERENCE_QUESTIONS,
  calculateReferenceFollowUpDate,
  canSendReferenceEmailForStatus,
  getReferenceAutomationMessage,
  getReadyReferenceStatus,
  normalizeYesNoRadioValue,
} from "./constants/recruitment";
import { toDate } from "./WorkHistoryGantt";
import { saveWorkHistoryReference } from "../../../backend/Recruitment/Referencing/saveWorkHistoryReference";
import { logReferenceAudit } from "../../../backend/Recruitment/Referencing/referenceAuditLog";
import { sendReferenceEmail } from "../../../backend/Recruitment/Referencing/sendReferenceEmail";
import {
  DEFAULT_REFERENCE_CONTACT,
  REFERENCE_CONTACTS,
} from "./constants/referenceContacts";
import "../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";

const STATUS_OPTIONS = RECRUITMENT_SELECTABLE_REFERENCE_STATUSES.map((status) => ({
  id: status.id,
  name: status.label,
}));

const REFERENCE_TYPE_OPTIONS = [
  { id: "regulated", name: "Regulated Role" },
  { id: "non-regulated", name: "Non-Regulated Role" },
  { id: "educational", name: "Educational Reference" },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { id: "Employed", name: "Employed" },
  { id: "Self-employed", name: "Self-employed" },
  { id: "Unemployed", name: "Unemployed" },
  { id: "In Education", name: "In Education" },
];

const FOLLOW_UP_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "twice-weekly", label: "Twice a week" },
  { value: "set-date", label: "Set a date" },
];

const PERMISSION_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const NOT_REQUIRED_STATUS = "not-required";

const VALIDATION_FIELD_LABELS = Object.freeze({
  firmName: "Name of Firm",
  from: "Employment From",
  to: "Employment To",
  appointedRepresentative: "Appointed Representative",
  appointedRepresentativeDetails: "Life Assurance Company or Network",
  educationFrom: "Education From",
  educationTo: "Education To",
  educationDetails: "Further Details",
  adviserPermission: "Adviser Permission",
  currentlyEmployedAtFirm: "Currently Employed at Firm",
  firmTelephone: "Firm Telephone",
  recipients: "To",
  cc: "CC",
  bcc: "BCC",
  messageBody: "Message Body",
  consentAttachment: "Reference Authorisation Form",
  saveConfirmed: "Confirmation checkbox",
});

const normaliseEmploymentType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const isEducationEmployment = (history) =>
  normaliseEmploymentType(
    history?.currentEmploymentStatus ||
      history?.employmentStatus ||
      history?.typeOfEmployment,
  ) === "ineducation";

const getInitialForm = (history, applicantName, referenceConsentAttachment) => {
  const educationalReference =
    isEducationEmployment(history) || history?.referenceType === "educational";

  return ({
  status: history?.referenceStatus || "awaiting-adviser",
  referenceSent: toDate(history?.referenceSent),
  referenceFollowUp:
    toDate(history?.referenceFollowUp) ||
    calculateReferenceFollowUpDate(
      history?.followUpTimeframe,
      toDate(history?.referenceSent),
      toDate(history?.followUpSetDate),
    ),
  lastFollowedUpOn: toDate(
    history?.lastFollowedUpOn || history?.referenceLastFollowedUp,
  ),
  referenceReceived: toDate(history?.referenceReceived),
  followUpTimeframe: history?.followUpTimeframe || "weekly",
  followUpSetDate: toDate(history?.followUpSetDate),
  skipReferenceFollowUp: Boolean(history?.skipReferenceFollowUp),
  internalAdminNotes: history?.internalAdminNotes || "",
  referenceType:
    isEducationEmployment(history)
      ? "educational"
      : history?.referenceType ||
        (history?.regulatedRole === true ||
        String(history?.regulatedRole).toLowerCase() === "yes"
          ? "regulated"
          : "non-regulated"),
  firmName: history?.nameOfFirm || "",
  jobTitle:
    history?.currentJobTitle ||
    history?.jobTitle ||
    history?.position ||
    history?.role ||
    "",
  employmentType:
    history?.currentEmploymentStatus ||
    history?.employmentStatus ||
    history?.typeOfEmployment ||
    "",
  educationFrom: toDate(history?.educationFrom || history?.from),
  educationTo: toDate(history?.educationTo || history?.to),
  educationDetails:
    history?.educationDetails || history?.furtherDetails || history?.nameOfFirm || "",
  questions: educationalReference
    ? history?.educationalReferenceQuestions || [...EDUCATIONAL_REFERENCE_QUESTIONS]
    : [],
  additionalQuestion: educationalReference
    ? history?.additionalReferenceQuestion || ""
    : "",
  from: toDate(history?.from),
  to: toDate(history?.to),
  firmTelephone: history?.officeTelephone || "",
  firmEmail: history?.emailAddress || "",
  appointedRepresentative:
    history?.appointedRepresentative === true
      ? "yes"
      : history?.appointedRepresentative === false
        ? "no"
        : ["yes", "no"].includes(
              String(history?.appointedRepresentative || "").toLowerCase(),
            )
          ? String(history.appointedRepresentative).toLowerCase()
          : "",
  appointedRepresentativeDetails:
    history?.lifeAssuranceCompany ||
    history?.appointedRepresentativeDetails ||
    history?.appointedRepresentativeNetwork ||
    "",
  adviserPermission: normalizeYesNoRadioValue(history?.adviserPermission),
  currentlyEmployedAtFirm:
    history?.currentlyEmployedAtFirm === true
      ? "yes"
      : history?.currentlyEmployedAtFirm === false
        ? "no"
        : ["yes", "no"].includes(
              String(history?.currentlyEmployedAtFirm || "").toLowerCase(),
            )
          ? String(history.currentlyEmployedAtFirm).toLowerCase()
          : "",
  adviserReason: history?.reasonForNoReference || "",
  recipients: history?.referenceRecipients || history?.emailAddress || "",
  sender:
    history?.referenceSender?.email ||
    history?.referenceSender ||
    DEFAULT_REFERENCE_CONTACT.email,
  cc: history?.referenceCc || "",
  bcc: history?.referenceBcc || "",
  subject:
    history?.referenceSubject ||
    `Reference Request for ${applicantName || "Applicant"}`,
  receivedReferenceAttachments: history?.receivedReferenceAttachments || [],
  attachments: [
    ...(history?.referenceAttachments || []).filter(
      (attachment) => attachment?.source !== "reference-consent-form",
    ),
    ...(referenceConsentAttachment ? [referenceConsentAttachment] : []),
  ],
  messageBody: history?.referenceMessageBody || "",
  saveConfirmed: false,
  });
};

const createInitialForm = (
  history,
  applicantName,
  nationalInsuranceNumber,
  referenceConsentAttachment,
) => {
  const form = getInitialForm(
    history,
    applicantName,
    referenceConsentAttachment,
  );
  const storedMessageBody = String(form.messageBody || "");
  const containsLegacyEducationalQuestions =
    form.referenceType !== "educational" &&
    /During which period\(s\) was .* enrolled at (?:the|your) educational institution\?/i.test(
      storedMessageBody,
    );
  return {
    ...form,
    messageBody: removeReferenceMessageBodySignature(
      (!containsLegacyEducationalQuestions && form.messageBody) ||
        buildDefaultReferenceMessageBody({
          form,
          applicantName,
          nationalInsuranceNumber,
          history,
        }),
    ),
  };
};

const WorkHistoryReferenceForm = ({
  history,
  applicantName,
  nationalInsuranceNumber,
  applicationId,
  referenceId,
  referenceConsentAttachment,
  onSaved,
  onCancel,
  isNewReference = false,
  applicationEmailBlocked = false,
  applicationEmailBlockedReason = "",
  referenceSender,
  from,
}) => {
  const [form, setForm] = useState(() =>
    createInitialForm(
      history,
      applicantName,
      nationalInsuranceNumber,
      referenceConsentAttachment,
    ),
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessageType, setSaveMessageType] = useState("success");
  const [sendSuccessMessage, setSendSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isReferenceSaved, setIsReferenceSaved] = useState(!isNewReference);
  const [areEditLogsOpen, setAreEditLogsOpen] = useState(false);
  const editLogsRef = useRef(null);
  const formTopRef = useRef(null);

  useEffect(() => {
    if (!saveMessage || saveMessageType !== "success") return undefined;
    const timeoutId = window.setTimeout(() => setSaveMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [saveMessage, saveMessageType]);

  useEffect(() => {
    setForm(
      createInitialForm(
        history,
        applicantName,
        nationalInsuranceNumber,
        referenceConsentAttachment,
      ),
    );
    setValidationErrors({});
    setHasUnsavedChanges(false);
    setIsReferenceSaved(!isNewReference);
  }, [history, applicantName, nationalInsuranceNumber, referenceConsentAttachment, isNewReference]);

  useEffect(() => {
    setSaveMessage("");
    setIsPreviewOpen(false);
  }, [referenceId]);

  useEffect(() => {
    if (!sendSuccessMessage) return undefined;
    const timeoutId = window.setTimeout(() => setSendSuccessMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [sendSuccessMessage]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setHasUnsavedChanges(true);
    setValidationErrors((current) =>
      field === "currentlyEmployedAtFirm" && value === "yes"
        ? { ...current, [field]: false, to: false }
        : current[field] ? { ...current, [field]: false } : current,
    );
  };

  const handlePermissionChange = (event) => {
    const permission = event.target.value;
    setForm((current) => ({
      ...current,
      adviserPermission: permission,
      status: getReadyReferenceStatus({
        status: current.status,
        adviserPermission: permission,
        followUpTimeframe: current.followUpTimeframe,
      }),
      adviserReason: current.adviserReason || "",
    }));
    setValidationErrors((current) => ({
      ...current,
      adviserPermission: false,
    }));
    setHasUnsavedChanges(true);
  };

  const handleFollowUpTimeframeChange = (event) => {
    const timeframe = event.target.value;
    setForm((current) => ({
      ...current,
      followUpTimeframe: timeframe,
      status: getReadyReferenceStatus({
        status: current.status,
        adviserPermission: current.adviserPermission,
        followUpTimeframe: timeframe,
      }),
      followUpSetDate:
        timeframe === "set-date" ? current.followUpSetDate : null,
      referenceFollowUp: calculateReferenceFollowUpDate(
        timeframe,
        current.referenceSent,
        current.followUpSetDate,
      ),
    }));
    setHasUnsavedChanges(true);
  };

  const handleFollowUpSetDateChange = (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      followUpSetDate: value,
      referenceFollowUp: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleStatusChange = (event) => {
    const status = event.target.value;
    setForm((current) => ({
      ...current,
      status,
      referenceReceived:
        status === "reference-received"
          ? current.referenceReceived || new Date()
          : current.status === "reference-received"
            ? null
            : current.referenceReceived,
      referenceFollowUp:
        status === "reference-received" || status === NOT_REQUIRED_STATUS
          ? null
          : current.referenceFollowUp,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSkipFollowUpChange = (checked) => {
    setForm((current) => ({
      ...current,
      skipReferenceFollowUp: checked,
    }));
    setHasUnsavedChanges(true);
  };

  const handleReferenceEmailSent = async () => {
    const result = await sendReferenceEmail({
      applicationId,
      referenceId,
      followUpTimeframe: form.followUpTimeframe || "",
      sender: form.sender || referenceSender || from,
    });
    const sentAt = result.referenceSent ? new Date(result.referenceSent) : new Date();
    const nextFollowUp = result.referenceFollowUp
      ? new Date(result.referenceFollowUp)
      : null;
    const savedHistory = {
      ...history,
      referenceStatus: result.status || "reference-sent",
      referenceSent: sentAt,
      referenceFollowUp: nextFollowUp,
      skipReferenceFollowUp: false,
    };
    setForm((current) => ({
      ...current,
      status: result.status || "reference-sent",
      referenceSent: sentAt,
      referenceFollowUp: nextFollowUp,
      skipReferenceFollowUp: false,
      saveConfirmed: false,
    }));
    setHasUnsavedChanges(false);
    setIsReferenceSaved(true);
    setIsPreviewOpen(false);
    const acceptedRecipientText = result.acceptedRecipients?.join(", ");
    setSendSuccessMessage(
      `Reference email sent successfully${acceptedRecipientText ? ` to ${acceptedRecipientText}` : ""}.`,
    );
    requestAnimationFrame(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    onSaved?.(referenceId, savedHistory);
  };

  const hasConsentAttachment = form.attachments?.some(
    (attachment) => attachment?.source === "reference-consent-form",
  );
  const isEmploymentReference = ["regulated", "non-regulated"].includes(
    form.referenceType,
  );
  const isRegulatedReference = form.referenceType === "regulated";
  const isEmploymentToRequired =
    isEmploymentReference && form.currentlyEmployedAtFirm !== "yes";
  const isReferenceNotRequired = form.status === NOT_REQUIRED_STATUS;
  const hasViewRequestErrors = Boolean(
    isReferenceNotRequired ||
    (isEmploymentReference && !form.firmName?.trim()) ||
      (isEmploymentReference && !form.from) ||
      (isEmploymentToRequired && !form.to) ||
      (isRegulatedReference &&
        form.appointedRepresentative === "yes" &&
        !form.appointedRepresentativeDetails?.trim()) ||
      (!isEmploymentReference && !form.educationFrom) ||
      (!isEmploymentReference && !form.educationTo) ||
      (!isEmploymentReference && !form.educationDetails?.trim()) ||
      !form.adviserPermission ||
      (isEmploymentReference && !form.currentlyEmployedAtFirm) ||
      !isValidReferencePhone(form.firmTelephone) ||
      !isValidEmailList(form.recipients, { required: true }) ||
      !isValidEmailList(form.cc) ||
      !isValidEmailList(form.bcc) ||
      !form.messageBody?.trim() ||
      (isEmploymentReference && !hasConsentAttachment),
  );

  const handleViewRequest = () => {
    if (isReferenceNotRequired) {
      return;
    }
    if (isEmploymentReference && !hasConsentAttachment) {
      setValidationErrors((current) => ({
        ...current,
        consentAttachment: true,
      }));
      return;
    }
    setIsPreviewOpen(true);
  };

  const showEditLogs = () => {
    setAreEditLogsOpen(true);
    requestAnimationFrame(() => {
      editLogsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleSave = async () => {
    const nextErrors = {
      firmName:
        !isReferenceNotRequired && isEmploymentReference && !form.firmName?.trim(),
      from: !isReferenceNotRequired && isEmploymentReference && !form.from,
      to: !isReferenceNotRequired && isEmploymentToRequired && !form.to,
      appointedRepresentativeDetails:
        !isReferenceNotRequired &&
        isRegulatedReference &&
        form.appointedRepresentative === "yes" &&
        !form.appointedRepresentativeDetails?.trim(),
      educationFrom:
        !isReferenceNotRequired && !isEmploymentReference && !form.educationFrom,
      educationTo:
        !isReferenceNotRequired && !isEmploymentReference && !form.educationTo,
      educationDetails:
        !isReferenceNotRequired &&
        !isEmploymentReference && !form.educationDetails?.trim(),
      adviserPermission: !isReferenceNotRequired && !form.adviserPermission,
      currentlyEmployedAtFirm:
        !isReferenceNotRequired &&
        isEmploymentReference &&
        !form.currentlyEmployedAtFirm,
      firmTelephone:
        !isReferenceNotRequired && !isValidReferencePhone(form.firmTelephone),
      recipients: isReferenceNotRequired
        ? ""
        : !form.recipients?.trim()
        ? "At least one recipient is required."
        : !isValidEmailList(form.recipients, { required: true })
          ? "Enter valid email addresses separated by commas."
          : "",
      cc: !isValidEmailList(form.cc)
        ? "Enter valid email addresses separated by commas."
        : "",
      bcc: !isValidEmailList(form.bcc)
        ? "Enter valid email addresses separated by commas."
        : "",
      messageBody: isReferenceNotRequired
        ? ""
        : !form.messageBody?.trim()
        ? "Message Body is required."
        : "",
      consentAttachment:
        !isReferenceNotRequired && isEmploymentReference && !hasConsentAttachment,
      saveConfirmed: !form.saveConfirmed,
    };
    const hasErrors = Object.values(nextErrors).some(Boolean);
    setValidationErrors(nextErrors);
    if (hasErrors) {
      if (!form.saveConfirmed) {
        void logReferenceAudit({
          applicationId,
          referenceId,
          referenceKind: "work-history",
          action: "save_confirmation_missing",
          changes: [{
            fieldPath: "confirmations.detailsConfirmedBeforeSave",
            previousValue: false,
            newValue: false,
          }],
          details: { saveAttempted: true },
        }).catch((auditError) => console.error("Reference audit failed", auditError));
      }
      setSaveMessage("");
      requestAnimationFrame(() => {
        formTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      return false;
    }

    setIsSaving(true);
    setSaveMessage("");
    setSaveMessageType("success");

    try {
      const savedHistory = await saveWorkHistoryReference({
        applicationId,
        referenceId,
        history,
        form,
      });
      setForm(
        createInitialForm(
          savedHistory,
          applicantName,
          nationalInsuranceNumber,
          referenceConsentAttachment,
        ),
      );
      onSaved?.(referenceId, savedHistory);
      setSaveMessage("Reference details saved successfully.");
      setSaveMessageType("success");
      requestAnimationFrame(() => {
        formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      setHasUnsavedChanges(false);
      setIsReferenceSaved(true);
      return true;
    } catch (saveError) {
      setSaveMessage(saveError?.message || "Unable to save reference details.");
      setSaveMessageType("error");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(
      createInitialForm(
        history,
        applicantName,
        nationalInsuranceNumber,
        referenceConsentAttachment,
      ),
    );
    setValidationErrors({});
    setSaveMessage("");
    setIsPreviewOpen(false);
    setHasUnsavedChanges(false);
    setIsReferenceSaved(!isNewReference);
    onCancel?.();
  };

  if (!history) return null;

  const currentSystemStatus = RECRUITMENT_REFERENCE_STATUSES.find(
    (status) => status.id === form.status && status.selectable === false,
  );
  const incompleteMandatoryFields = Object.entries(validationErrors)
    .filter(([, error]) => Boolean(error))
    .map(([field]) => VALIDATION_FIELD_LABELS[field] || field);

  return (<div className="column gap-l flex-1">
    <div ref={formTopRef} className="work-history-reference-form column gap-l flex-1 bg-white p-m corner card-corner-radius">
      <AlertBanner
        type="info"
        message={getReferenceAutomationMessage(form)}
        showClose={false}
      />
      {saveMessage && (
        <AlertBanner type={saveMessageType} message={saveMessage} showClose={false} />
      )}
      {incompleteMandatoryFields.length > 0 && (
        <AlertBanner
          type="error"
          message={`Please complete or correct: ${incompleteMandatoryFields.join(", ")}.`}
          showClose={false}
        />
      )}
      {sendSuccessMessage && (
        <AlertBanner
          type="success"
          message={sendSuccessMessage}
          showClose={false}
        />
      )}
      {validationErrors.consentAttachment && (
        <AlertBanner
          type="error"
          message="A Reference Authorisation Form must be attached before this email request can be viewed or sent."
          showClose={false}
        />
      )}
      <div className="reference-status-card fit-column gap-m bg-blue-200 p-m corner card-corner-radius">
        <div className="reference-status-card-header display-flex justify-end">
          <button
            type="button"
            className="reference-log-icon-button"
            onClick={showEditLogs}
            aria-label="Show reference logs"
            title="Logs"
          >
            <HistoryIcon />
          </button>
        </div>
        <div className="grid3 gap-m">
          <CustomSelect
            label="Status of Reference"
            options={STATUS_OPTIONS}
            value={form.status}
            selectedLabel={currentSystemStatus?.label}
            onChange={handleStatusChange}
            clearable={false}
            searchable={false}
          />
          <NewDateField
            label="Date Reference Requested"
            value={form.referenceSent}
            readOnly
            disabled={true}
          />
          <NewDateField
            label="Date Reference Received"
            value={form.referenceReceived}
            onChange={(event) =>
              updateField("referenceReceived", event.target.value)
            }
          />
        </div>

        <div className="grid3 gap-m">
          <div className="flex-start-column gap-xs">
            <div className="FP_Content">Set Follow Ups Timeframe</div>
            {FOLLOW_UP_OPTIONS.slice(0, 2).map((option) => (
              <RadioGroup
                key={option.value}
                name="followUpTimeframe"
                options={[option]}
                value={form.followUpTimeframe}
                onChange={handleFollowUpTimeframeChange}
                deselectable
              />
            ))}
            <div className="display-flex align-center gap-xs">
              <RadioGroup
                name="followUpTimeframe"
                options={[FOLLOW_UP_OPTIONS[2]]}
                value={form.followUpTimeframe}
                onChange={handleFollowUpTimeframeChange}
                deselectable
              />
              {form.followUpTimeframe === "set-date" && (
                <NewDateField
                  label=""
                  value={form.followUpSetDate}
                  onChange={handleFollowUpSetDateChange}
                  required
                />
              )}
            </div>
          </div>

          <div className="flex-start-column gap-xs">
            <NewDateField
              label="Date of Next Follow Up"
              value={form.referenceFollowUp}
              readOnly
              disabled
            />
            <div className="display-flex align-center gap-xs">
              <ToggleButton
                checked={form.skipReferenceFollowUp}
                onChange={handleSkipFollowUpChange}
              />
              <div className="FP_Content">Skip next follow up</div>
            </div>
          </div>
          <NewDateField
            label="Last Followed Up On"
            value={form.lastFollowedUpOn}
            readOnly
            disabled
          />
        </div>

        {form.status === "reference-received" && (
          <ReceivedReferenceUpload files={form.receivedReferenceAttachments} onChange={(files) => updateField("receivedReferenceAttachments", files)} />
        )}
        <NewInputTextArea
          label="Internal Admin Notes"
          value={form.internalAdminNotes}
          onChange={(event) =>
            updateField("internalAdminNotes", event.target.value)
          }
          placeholder="These notes are for internal use only"
          maxLength={2000}
        />
      </div>

      <div className="FP_Body_Semibold text-black">
        Details for Reference Email
      </div>

      <div className="grid2 gap-m">
        <CustomSelect
          label="Type of Reference"
          options={REFERENCE_TYPE_OPTIONS}
          value={form.referenceType}
          onChange={(event) => updateField("referenceType", event.target.value)}
          clearable={false}
          searchable={false}
        />
      </div>
      {isEmploymentReference ? <>
      <div className="grid2 gap-m">
        <NewInputField
          label="Job Title"
          value={form.jobTitle}
          onChange={(event) => updateField("jobTitle", event.target.value)}
        />
        <CustomSelect
          label="Employment Type"
          options={EMPLOYMENT_TYPE_OPTIONS}
          value={form.employmentType}
          onChange={(event) => {
            const employmentType = event.target.value;
            setForm((current) => ({
              ...current,
              employmentType,
              referenceType:
                normaliseEmploymentType(employmentType) === "ineducation"
                  ? "educational"
                  : current.referenceType === "educational"
                    ? "non-regulated"
                    : current.referenceType,
            }));
            setHasUnsavedChanges(true);
          }}
          clearable={false}
          searchable={false}
        />
      </div>
      <div className="grid2 gap-m">
        <NewInputField
          label="Name of Firm"
          value={form.firmName}
          onChange={(event) => updateField("firmName", event.target.value)}
          required={!isReferenceNotRequired}
          error={validationErrors.firmName ? "Name of Firm is required." : ""}
        />{" "}
        <NewMobileNumberInput
          label="Contact Telephone of Firm"
          value={form.firmTelephone}
          onChange={(value) => updateField("firmTelephone", value)}
          error={validationErrors.firmTelephone ? "Enter a valid telephone number." : ""}
        />
      </div>

      <div className="grid2 gap-m">
        <NewDateField
          label="Employment From"
          value={form.from}
          onChange={(event) => updateField("from", event.target.value)}
          required={!isReferenceNotRequired}
          error={validationErrors.from}
          helperText={validationErrors.from ? "Employment From is required." : ""}
        />
        <NewDateField
          label="Employment To"
          value={form.to}
          onChange={(event) => updateField("to", event.target.value)}
          required={!isReferenceNotRequired && isEmploymentToRequired}
          error={validationErrors.to}
          helperText={validationErrors.to ? "Employment To is required." : ""}
        />
      </div>
      </> : <>
        <div className="grid2 gap-m">
          <NewDateField
            label="Education From"
            value={form.educationFrom}
            onChange={(event) => updateField("educationFrom", event.target.value)}
            required={!isReferenceNotRequired}
            error={validationErrors.educationFrom}
            helperText={validationErrors.educationFrom ? "Education From is required." : ""}
          />
          <NewDateField
            label="Education To"
            value={form.educationTo}
            onChange={(event) => updateField("educationTo", event.target.value)}
            required={!isReferenceNotRequired}
            error={validationErrors.educationTo}
            helperText={validationErrors.educationTo ? "Education To is required." : ""}
          />
        </div>
        <NewInputTextArea
          label="Further Details"
          value={form.educationDetails}
          onChange={(event) => updateField("educationDetails", event.target.value)}
          placeholder="Name of school or university, course and any relevant programme details"
          maxLength={2000}
          required={!isReferenceNotRequired}
          error={validationErrors.educationDetails ? "Further Details are required." : ""}
        />
      </>}

      {isRegulatedReference && (
        <div className="fit-column gap-m">
          <RadioGroup
            name="appointedRepresentative"
            label="Are you or the firm an appointed representative?"
            options={PERMISSION_OPTIONS}
            value={form.appointedRepresentative}
            onChange={(event) =>
              updateField("appointedRepresentative", event.target.value)
            }
            inline
            deselectable
          />
          <NewInputTextArea
            label="If yes, please confirm the life assurance company or network"
            value={form.appointedRepresentativeDetails}
            onChange={(event) =>
              updateField("appointedRepresentativeDetails", event.target.value)
            }
            maxLength={1000}
            required={!isReferenceNotRequired && form.appointedRepresentative === "yes"}
            error={validationErrors.appointedRepresentativeDetails}
            helperText={
              validationErrors.appointedRepresentativeDetails
                ? "Please enter the life assurance company or network."
                : ""
            }
          />
        </div>
      )}

      <div className="grid2 gap-m">
        <RadioGroup
          name="adviserPermission"
          label="Adviser’s permission to contact for reference"
          options={PERMISSION_OPTIONS}
          value={form.adviserPermission}
          onChange={handlePermissionChange}
          required={!isReferenceNotRequired}
          inline
          error={validationErrors.adviserPermission}
          deselectable
          helperText={
            validationErrors.adviserPermission
              ? "Please select an adviser permission option."
              : ""
          }
        />
        {isEmploymentReference && <RadioGroup
          name="currentlyEmployedAtFirm"
          label="Is the adviser currently employed at this firm?"
          options={PERMISSION_OPTIONS}
          value={form.currentlyEmployedAtFirm}
          onChange={(event) =>
            updateField("currentlyEmployedAtFirm", event.target.value)
          }
          required={!isReferenceNotRequired}
          inline
          error={validationErrors.currentlyEmployedAtFirm}
          deselectable
          helperText={
            validationErrors.currentlyEmployedAtFirm
              ? "Please confirm whether the adviser is currently employed at this firm."
              : ""
          }
        />}
      </div>

      {isEmploymentReference && <NewInputTextArea
        label="Adviser’s reason for No Reference"
        value={form.adviserReason || ""}
        onChange={(event) => updateField("adviserReason", event.target.value)}
        placeholder="Adviser’s notes from application form"
        disabled={form.adviserPermission === "yes"}
        // maxLength={1000}
      />}

      <Divider />
      <div className="grid2 gap-m">
        <NewInputField
          label="To"
          value={form.recipients}
          onChange={(event) => updateField("recipients", event.target.value)}
          placeholder="Separate email addresses with commas"
          required={!isReferenceNotRequired}
          error={validationErrors.recipients || ""}
          maxLength={2000}
        />
      <CustomSelect
        label="From"
        options={REFERENCE_CONTACTS}
        value={form.sender}
        onChange={(event) => updateField("sender", event.target.value)}
        clearable={false}
        searchable={false}
      />

      </div>
        <NewInputField
          label="Subject"
          value={form.subject}
          onChange={(event) => updateField("subject", event.target.value)}
          maxLength={500}
        />

      <NewInputField
        label="CC (Separate email addresses with commas)"
        value={form.cc}
        onChange={(event) => updateField("cc", event.target.value)}
        placeholder="Separate email addresses with commas"
        error={validationErrors.cc || ""}
        maxLength={2000}
      />

      <NewInputField
        label="BCC (Separate email addresses with commas)"
        value={form.bcc}
        onChange={(event) => updateField("bcc", event.target.value)}
        placeholder="Separate email addresses with commas"
        error={validationErrors.bcc || ""}
        maxLength={2000}
      />

      <div className="fit-column gap-s">
        <FileUpload
          label="Attachments"
          description="Drag & drop reference documents here or"
          browseText="Browse"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          multiple
          files={form.attachments.filter((file) => file?.source !== "reference-consent-form")}
          onFilesChange={(files) => {
            const selectedFiles = Array.isArray(files) ? files : [files];
            updateField("attachments", [
              ...form.attachments,
              ...selectedFiles.filter(Boolean),
            ]);
          }}
        />
        <UploadedFileList
          files={form.attachments.filter((file) => file?.source !== "reference-consent-form")}
          canRemove={(file) => file?.source !== "reference-consent-form"}
          onRemove={(file) =>
            updateField(
              "attachments",
              form.attachments.filter(
                (attachment) => attachment !== file,
              ),
            )
          }
        />
      </div>

      <NewInputTextArea
        label="Message Body"
        value={form.messageBody}
        onChange={(event) =>
          updateField("messageBody", event.target.value)
        }
        placeholder="Edit the email template message body"
        maxLength={5000}
        className="reference-message-body-field"
        required={!isReferenceNotRequired}
        error={validationErrors.messageBody || ""}
      />

      <div className="reference-sticky-actions fit-column gap-m">
      <label className={`reference-save-confirmation display-flex align-center gap-s FP_Content ${validationErrors.saveConfirmed ? "reference-save-confirmation--error" : ""}`}>
        <input
          type="checkbox"
          checked={form.saveConfirmed}
          onChange={(event) => updateField("saveConfirmed", event.target.checked)}
        />
        <span>I confirm that all reference details are correct before saving.</span>
      </label>

      <div className="row-space-between gap-m">
        <div className="display-flex gap-s">
          <SecondaryButton
            type="button"
            label={isSaving ? "Saving..." : "Save"}
            disabled={isSaving}
            onClick={handleSave}
          />
          <SecondaryButton
            type="button"
            label="Cancel"
            disabled={isSaving}
            onClick={handleCancel}
          />
        </div>
        <PrimaryButton
          type="button"
          label="View Request"
          disabled={hasViewRequestErrors}
          onClick={handleViewRequest}
        />
      </div>
      </div>
      <ReferenceEmailPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onSend={handleReferenceEmailSent}
        form={form}
        applicantName={applicantName}
        nationalInsuranceNumber={nationalInsuranceNumber}
        history={history}
        applicationId={applicationId}
        referenceId={referenceId}
        referenceKind="work-history"
        needsSave={!isReferenceSaved || hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onSaveConfirmationChange={(checked) => updateField("saveConfirmed", checked)}
        canSend={
          !applicationEmailBlocked &&
          isReferenceSaved &&
          !hasUnsavedChanges &&
          canSendReferenceEmailForStatus(form.status)
        }
        sendDisabledReason={
          applicationEmailBlocked
            ? applicationEmailBlockedReason
            : !isReferenceSaved
            ? "Save this reference before sending the email."
            : hasUnsavedChanges
              ? "You have unsaved changes. Close this preview and save the reference before sending."
              : !canSendReferenceEmailForStatus(form.status)
                ? "The current reference status does not allow emails to be sent."
                : ""
        }
      />
    </div>
      <ReferenceEditLogs
        applicationId={applicationId}
        referenceId={referenceId}
        open={areEditLogsOpen}
        onToggle={() => setAreEditLogsOpen((current) => !current)}
        sectionRef={editLogsRef}
      /></div>
  );
};

export default WorkHistoryReferenceForm;
